import classNames from 'classnames';
import React, { useState } from 'react';
import NumberFormat from 'react-number-format';

import { Icon } from './Icon';
import { SelectInputOption } from './SelectInput';

const defaultMaxLength = 50;

export interface TextInputProps {
  label?: string;
  state?: 'default' | 'error' | 'success' | string;
  placeholder?: string;
  value?: string | SelectInputOption | null;
  leftIcon?: string;
  rightIcon?: string;
  type?: string;
  disabled?: boolean;
  inputGroupClassName?: string;
  maxLength?: string;
  field?: any; // TODO: get formik field type
  form?: any; // TODO: get formik form type
  onRightIconClick?: () => void;
}

export const InputGroup: React.FC<TextInputProps> = ({
  label = '',
  state = 'default',
  leftIcon = '',
  rightIcon = '',
  children,
  type,
  onRightIconClick = undefined,
  disabled = false,
  inputGroupClassName = '',
}) => {
  const IconLeft = () =>
    leftIcon ? <Icon name={leftIcon} className="btn-icon oejr-input-icon-left" /> : <></>;
  const IconRight = () =>
    rightIcon ? (
      <Icon
        name={rightIcon}
        className="btn-icon oejr-input-icon-right"
        onClick={onRightIconClick}
      />
    ) : (
      <></>
    );
  const Label = () => (label ? <label>{label}</label> : <></>);

  return (
    <div
      className={classNames(
        'oejr-input-group',
        `oejr-input-group-${state}`,
        disabled && 'oejr-input-group-disabled',
        inputGroupClassName,
      )}
    >
      <Label />
      <div
        className={classNames(
          'oejr-input-wrapper',
          type && `oejr-input-wrapper-${type}`,
          leftIcon && `oejr-input-wrapper-icon-left`,
          rightIcon && `oejr-input-wrapper-icon-right`,
          label && `oejr-input-wrapper-with-label`,
        )}
      >
        <IconLeft />
        {children}
        <IconRight />
      </div>
    </div>
  );
};

export const TextInput: React.FC<TextInputProps> = (props) => {
  if (props.type === 'password') {
    return <PasswordInput {...props} />;
  } else if (props.type === 'tel') {
    return <TelInput {...props} />;
  } else if (props.type === 'custom_text-letters-only') {
    return <LettersInput {...props} />;
  }

  const { inputGroupClassName, field, form, maxLength, ...otherProps } = props; // ref: https://github.com/jaredpalmer/formik/issues/1418#issuecomment-517675354
  const state = getFinalState(props.state, form?.errors, field, form?.touched);

  return (
    <InputGroup {...props} state={state}>
      <input
        type="text"
        {...field}
        {...otherProps}
        onChange={(e) => {
          if (e.target.value.indexOf('=') !== -1) {
            // we want to avoid users to insert equal sign on our inputs
            e.target.value = e.target.value.replace(/=/g, '');
          }
          return field.onChange(e);
        }}
        maxLength={maxLength || defaultMaxLength}
      />
    </InputGroup>
  );
};

const TelInput: React.FC<TextInputProps> = (props) => {
  const { inputGroupClassName, field, form, maxLength, ...otherProps } = props;
  const state = getFinalState(props.state, form?.errors, field, form?.touched);

  return (
    <InputGroup {...props} state={state}>
      <NumberFormat
        {...field}
        {...otherProps}
        allowNegative={false}
        maxLength={maxLength || defaultMaxLength}
        type="tel"
      />
    </InputGroup>
  );
};

const LettersInput: React.FC<TextInputProps> = (props) => {
  const { inputGroupClassName, field, form, maxLength, ...otherProps } = props;
  const state = getFinalState(props.state, form?.errors, field, form?.touched);

  return (
    <InputGroup {...props} state={state}>
      <input
        type="text"
        {...field}
        {...otherProps}
        onChange={(e) => {
          // Explanation of the regular expression:
          // [^...] : A negated or complemented character set. Matches anything not in the brackets.
          // A-Za-z : Matches all lowercase and uppercase letters in the basic Latin alphabet.
          // \s : Matches any white space character (spaces, tabs, line breaks).
          // \u00C0-\u00FF : Unicode range for Latin-1 Supplement, which includes accented characters used in Spanish and Portuguese.
          // \u00B4 : The acute accent, used in both Spanish and Portuguese.
          // \u02DC : Small tilde, similar to the tilde used in Portuguese.
          // gi : Global and case-insensitive search.
          e.target.value = e.target.value.replace(/[^A-Za-z\s\u00C0-\u00FF\u00B4\u02DC]/gi, '');
          return field.onChange(e);
        }}
        maxLength={maxLength || defaultMaxLength}
      />
    </InputGroup>
  );
};

const PasswordInput: React.FC<TextInputProps> = (props) => {
  const [showPassword, setShowPassword] = useState(false);

  const onRightIconClick = () => {
    setShowPassword(!showPassword);
  };

  const { inputGroupClassName, field, form, maxLength, ...otherProps } = props;
  const state = getFinalState(props.state, form?.errors, field, form?.touched);

  return (
    <InputGroup
      {...props}
      onRightIconClick={onRightIconClick}
      rightIcon={showPassword ? 'eye' : 'eye-slash'}
      state={state}
    >
      <input
        {...field}
        {...otherProps}
        maxLength={maxLength || defaultMaxLength}
        type={showPassword ? 'text' : 'password'}
      />
    </InputGroup>
  );
};

export const getFinalState = (inputState: any, formErrors: any, field: any, formTouched: any) => {
  // TODO: get formik types
  if (!formErrors && !formTouched) {
    return inputState;
  }
  return (
    (!inputState && formErrors[field?.name] && formTouched[field?.name] && 'error') ||
    (field?.value && 'filled') ||
    inputState
  );
};
